/**
 * Clue — full-screen clue takeover.
 *
 * Phase: the player has tapped a tile (and, for Daily Double, set a wager).
 * The clue text fills the screen — white all-caps body on the deep board
 * blue, 32-48px depending on length. A small category + value chip sits at
 * the top so the player always knows the context.
 *
 * "Begin answering" button drops them into the Answer phase (where the
 * input field appears). For accessibility — and the audience — clue stays
 * up while the player types; we don't hide it.
 */
import { motion } from 'motion/react';

import { CATEGORY_LABELS } from '@/games/categories/boards';
import { formatDollars, type BoardTileState } from '@/games/categories/engine';

import { zoomSpring } from './motion';

import './Clue.css';

export interface ClueProps {
  tile: BoardTileState;
  /** True once the player has tapped "Begin answering" — the input is up. */
  showAnswerInput: boolean;
  onProceedToAnswer: () => void;
}

export function Clue({
  tile,
  showAnswerInput,
  onProceedToAnswer,
}: ClueProps): React.JSX.Element {
  return (
    <motion.section
      className="tb-clue"
      data-input-up={showAnswerInput ? 'true' : 'false'}
      initial={{ scale: 0.85, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      exit={{ scale: 0.94, opacity: 0 }}
      transition={zoomSpring}
      aria-label="Clue"
    >
      <header className="tb-clue__chip">
        <span className="tb-clue__chip-category">
          {CATEGORY_LABELS[tile.category].toUpperCase()}
        </span>
        <span className="tb-clue__chip-divider" aria-hidden="true">·</span>
        <span className="tb-clue__chip-value">{formatDollars(tile.value)}</span>
      </header>

      <div className="tb-clue__body">
        <p className="tb-clue__stem">{tile.clue.stem.toUpperCase()}</p>
      </div>

      {!showAnswerInput && (
        <footer className="tb-clue__footer">
          <button
            type="button"
            className="tb-clue__begin"
            onClick={onProceedToAnswer}
          >
            Begin answering
          </button>
          <p className="tb-clue__hint">
            Answer in question form — &ldquo;What is …?&rdquo; or &ldquo;Who is …?&rdquo;
          </p>
        </footer>
      )}
    </motion.section>
  );
}
